import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uid } from "uuid";
import { IAddress, IAppointment, IEventSlot } from "../../interfaces";
import { loggerService, networkService } from "../../services";
import dateService, {
  API_DATE_FORMAT,
  USER_TIME_FORMAT_12H,
} from "../../services/date.service";
import websocketLoggerService from "../../services/websocket-logger";
import { useConsumerStore } from "../../store/auth";
import useReferralCodeStore from "../../store/referralCode.store";
import Backdrop from "../shared/components/backdrop.component";
import {
  getUserFriendlyErrorMessage,
  isWithin24Hours,
} from "../shared/utils/helper.util";
import ErrorWarning from "../shared/components/error-warning.component";

const RescheduleSession: React.FC = () => {
  const navigate = useNavigate(),
    consumer = useConsumerStore((state) => state.consumer),
    [selectedDate, setSelectedDate] = useState<string>(), // YYYY-MM-DD
    [selectedMonth, setSelectedMonth] = useState<string>(), // YYYY-MM-01
    [selectedSlot, setSelectedSlot] = useState<IEventSlot>(),
    [dataSource, setDataSource] = useState<Record<string, IEventSlot[]>>({}),
    setConsumer = useConsumerStore((state) => state.setConsumer),
    [errorMessage, setErrorMessage] = useState<string>();

  // rest api states
  // const [appointmentTypeTemplate, setAppointmentTypeTemplate] =
  //   useState<IAppointmentTypeTemplate>(null);
  const { user } = useAuth0();
  const authenticatedUser = {
    externalUserId: consumer.id,
    authenticationId: user?.sub,
  };

  const [addresses, setAddresses] = useState<IAddress[]>(null);
  const [selectedAddress, setSelectedAddress] = useState<IAddress>(null);
  const [availableSlots, setAvailableSlots] = useState<IEventSlot[]>(null);
  const allReferralCodes = useReferralCodeStore(
    (state) => state.allReferralCodes
  );

  const [previousAppointmentData, setPreviousAppointmentData] =
    useState<IAppointment>(null);
  const { id: appointmentToken } = useParams();

  const getAppointmentDetails = () => {
    // const appointmentToken: string = window?.location?.pathname?.split("/")[2];

    networkService
      .get<IAppointment>(
        `/api/scheduling/patients/${consumer.id}/appointments/${appointmentToken}`
      )
      .then((res: IAppointment) => {
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request: getAddresses: GET /api/scheduling/patients/${consumer.id}/appointments/${appointmentToken}`,
          eventSubType: `Get appointment details by token for reschedule appointmnet`,
          eventData: "fetched user's appointment successfully",
        });
        setPreviousAppointmentData(res);
        const serviceProviderId: string = res.appointmentType.userId.toString();
        getAddresses(serviceProviderId);
      })
      .catch((err) => {
        loggerService.error(err);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request: getAddresses: GET /api/scheduling/patients/${consumer.id}/appointments/${appointmentToken}`,
          eventSubType: `Get appointment details by token`,
          eventData: `could not fetch user's appointment, Error: ${JSON.stringify(
            err
          )}`,
        });
      });
  };

  React.useEffect(() => {
    getAppointmentDetails();
  }, []);

  function findReferralCodeByRegion(regionName) {
    for (const referralCode of allReferralCodes) {
      if (referralCode["pdtf-region"] === regionName) {
        return referralCode["pdtf-refferalcode"];
      }
    }
    return null;
  }

  const getAddresses = async (serviceProviderId: string) => {
    networkService
      .get<IAddress[]>(
        `/api/scheduling/patients/${consumer.id}/service-providers/${serviceProviderId}/addresses`
      )
      .then((addresses) => {
        const referralCodeKey = findReferralCodeByRegion(
          consumer?.regionName
        )?.split("-")[0];

        const addressesByReferralCode = addresses.filter((address) =>
          address.title.includes(referralCodeKey)
        );

        setAddresses(addressesByReferralCode);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request: getAddresses: GET /api/scheduling/patients/${consumer.id}/service-providers/${serviceProviderId}/addresses`,
          eventSubType: `Get addresses for reschedule booking`,
          eventData: "fetched available Addresses successfully",
        });
      })
      .catch((err) => {
        loggerService.error(err);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request: getAddresses: GET /api/scheduling/patients/${consumer.id}/service-providers/${serviceProviderId}/addresses`,
          eventSubType: `Error: ${JSON.stringify(err)}`,
          eventData: "Could not get addresses",
        });
        toast.error("Something went wrong");
      });
  };

  React.useEffect(() => {
    if (addresses?.length === 1) setSelectedAddress(addresses[0]);
  }, [addresses]);

  // fetch available slots
  const getAvailableSlotsAfter3Days = () => {
    const currentDay = dateService.getMomentDate().format("dddd");
    if (currentDay === "Monday") {
      return 4;
    } else {
      return 6;
    }
  };
  React.useEffect(() => {
    if (!previousAppointmentData || !selectedAddress) return;
    const fetchRescheduleSlots = async () => {
      const addressTitle: string = selectedAddress?.title;

      const appointmentId: string =
        previousAppointmentData.appointmentTypeId?.toString();

      try {
        let availableSlots = await networkService.get<IEventSlot[]>(
          `/api/scheduling/patients/${
            consumer.id
          }/available-slots?appointmentTypeId=${appointmentId}&fromDate=${dateService
            .getMomentDate()
            .add(getAvailableSlotsAfter3Days(), "days")
            .format(API_DATE_FORMAT)}&toDate=${dateService
            .getMomentDate()
            .add(2, "months")
            .format(API_DATE_FORMAT)}&addressTitle=${addressTitle}`
        );

        const sortedSlots = availableSlots.sort((p1, p2) => {
          const startDateP1 = new Date(p1.startDateTime);
          const startDateP2 = new Date(p2.startDateTime);
          return startDateP1 < startDateP2
            ? -1
            : startDateP1 > startDateP2
            ? 1
            : 0;
        });

        availableSlots = sortedSlots;

        setAvailableSlots(availableSlots);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `/api/scheduling/patients/${
            consumer.id
          }/available-slots?appointmentTypeTemplateId=${appointmentId}&fromDate=${dateService
            .getMomentDate()
            .format(API_DATE_FORMAT)}&toDate=${dateService
            .getMomentDate()
            .add(2, "months")
            .format(API_DATE_FORMAT)}&addressTitle=${addressTitle}`,
          eventSubType: `Get availableSlots for reschedule appointment`,
          eventData: "successfully fetched available slots",
        });
      } catch (error) {
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `/api/scheduling/patients/${
            consumer.id
          }/available-slots?appointmentTypeTemplateId=${appointmentId}&fromDate=${dateService
            .getMomentDate()
            .format(API_DATE_FORMAT)}&toDate=${dateService
            .getMomentDate()
            .add(2, "months")
            .format(API_DATE_FORMAT)}&addressTitle=${addressTitle}`,
          eventSubType: `Get availableSlots failed in reschedule booking`,
          eventData: "Could not GETavailable slots",
        });
      }
    };
    fetchRescheduleSlots();
  }, [previousAppointmentData, selectedAddress]);

  // init state variables
  React.useEffect(() => {
    const momentObj = dateService.getMomentDate();
    setSelectedSlot(null);
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  }, []);

  // populate date to slot dict - `dataSource`
  React.useEffect(() => {
    if (!availableSlots) return;

    const datetime = dateService.convertUTCDateToUserTimezone(
      previousAppointmentData?.date
    );
    const formattedDate = datetime.format(API_DATE_FORMAT);
    setSelectedDate(formattedDate);
    setSelectedMonth(dateService.getStartOfMonth(datetime));

    const dataSource = {};
    availableSlots.forEach((slot) => {
      const browserDate = dateService.convertUtcDateTimeToBrowserFormat(
        slot.date,
        slot.startTime,
        "YYYYMMDD"
      );
      if (!dataSource[browserDate]) {
        dataSource[browserDate] = [];
      }
      dataSource[browserDate].push(slot);
    });

    setDataSource(dataSource);
  }, [availableSlots]);

  // to do refactor
  const [filteredSlots, setFilteredSlots] = useState<any>();
  useEffect(() => {
    // pass moment time to this function
    const renderTimeSlots = async (selectedDate: string) => {
      const momentObj = dateService.convertDateStrToMoment(
          selectedDate,
          API_DATE_FORMAT
        ),
        slots = dataSource?.[momentObj.format("YYYYMMDD")],
        isSlotSelected = function (slot: IEventSlot) {
          if (!selectedSlot) return false;
          return slot.startTime === selectedSlot.startTime;
        };

      if (!Array.isArray(slots) || !slots.length) {
        return <div></div>;
      }

      const populateSlots = async (slots, start, end) => {
        const availableSlots = await slots.reduce((acc, slot) => {
          const currentTime = dateService.convertToMomentObject(
            dateService.convertUtcDateTimeToBrowserFormat(
              slot.date,
              slot.startTime,
              USER_TIME_FORMAT_12H
            )
          );

          const startTime = dateService.convertToMomentObject(start);
          const endTime = dateService.convertToMomentObject(end);

          if (currentTime.isBetween(startTime, endTime)) {
            acc.push(
              <button
                key={uid()}
                onClick={() => setSelectedSlot(slot)}
                className={`w-full py-2.5 text-sm rounded-lg hover:border-transparent focus:outline-none border border-gray-300  hover:border-primary-800 hover:bg-[#EAEBFF] hover:text-primary-800 hover:font-semibold duration-300 transition-colors group ${
                  isSlotSelected(slot)
                    ? "border-primary-800 bg-[#EAEBFF] text-primary-800 font-semibold"
                    : "text-black"
                }`}>
                {dateService.convertUtcDateTimeToBrowserFormat(
                  slot.date,
                  slot.startTime,
                  USER_TIME_FORMAT_12H
                )}
              </button>
            );
          }
          return acc;
        }, []);

        return availableSlots;
      };

      const morningSlots = await populateSlots(slots, "00:59 AM", "12:00 PM");
      const afternoonSlots = await populateSlots(slots, "11:59 AM", "04:00 PM");
      const eveningSlots = await populateSlots(slots, "03:59 PM", "11:59 PM");

      return (
        <>
          <div>
            {/* Morning Slots */}
            {morningSlots.length > 0 && (
              <SlotsTemplate
                title="Morning"
                slots={morningSlots}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
                      fill="black"
                    />
                  </svg>
                }
              />
            )}
            {/* Afternoon  Slots */}
            {afternoonSlots.length > 0 && (
              <SlotsTemplate
                title="Afternoon"
                slots={afternoonSlots}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 20 20">
                    <g clipPath="url(#clip0_95_2227)">
                      <path
                        fill="#161616"
                        fillRule="evenodd"
                        d="M10 13.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zM10 15a5 5 0 100-10 5 5 0 000 10zm0-15a.625.625 0 01.625.625v2.5a.625.625 0 11-1.25 0v-2.5A.625.625 0 0110 0zm0 16.25a.624.624 0 01.625.625v2.5a.624.624 0 11-1.25 0v-2.5A.625.625 0 0110 16.25zM20 10a.624.624 0 01-.625.625h-2.5a.624.624 0 110-1.25h2.5A.625.625 0 0120 10zM3.75 10a.625.625 0 01-.625.625h-2.5a.625.625 0 110-1.25h2.5A.625.625 0 013.75 10zm13.321-7.071a.625.625 0 010 .884L15.304 5.58a.626.626 0 01-.884-.885l1.768-1.767a.625.625 0 01.883 0zM5.58 14.42a.625.625 0 010 .884L3.812 17.07a.625.625 0 01-.883-.884l1.767-1.767a.625.625 0 01.884 0zm11.491 2.651a.625.625 0 01-.884 0l-1.767-1.767a.625.625 0 01.884-.884l1.767 1.768a.625.625 0 010 .883zM5.58 5.581a.625.625 0 01-.884 0L2.93 3.812a.625.625 0 11.884-.883L5.58 4.696a.625.625 0 010 .885z"
                        clipRule="evenodd"></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_95_2227">
                        <path fill="#fff" d="M0 0H20V20H0z"></path>
                      </clipPath>
                    </defs>
                  </svg>
                }
              />
            )}
            {eveningSlots.length > 0 && (
              <SlotsTemplate
                title="Evening"
                slots={eveningSlots}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
                      fill="black"
                    />
                  </svg>
                }
              />
            )}
          </div>
        </>
      );
    };

    const getSlots = async () => {
      const _slots = await renderTimeSlots(selectedDate);
      setFilteredSlots(_slots);
    };

    getSlots();
  }, [dataSource, selectedDate, selectedSlot]);

  // const populateSlots = (slots, start, end) => {
  //   const availableSlots = slots?.reduce((acc, slot) => {
  //     const currentTime = dateService.convertToMomentObject(
  //       dateService.convertUtcDateTimeToBrowserFormat(
  //         slot.date,
  //         slot.startTime,
  //         USER_TIME_FORMAT_12H
  //       )
  //     );

  //     const startTime = dateService.convertToMomentObject(start);
  //     const endTime = dateService.convertToMomentObject(end);

  //     if (currentTime.isBetween(startTime, endTime)) {
  //       acc.push("1");
  //     }
  //     return acc;
  //   }, []);

  //   return availableSlots;
  // };

  const onDateSelect = (date: string) => {
    if (date === selectedDate) {
      return;
    }

    setSelectedSlot(null);
    setSelectedDate(date);
  };

  // const doesSlotExist = (
  //   dataSource,
  //   year: string,
  //   month: string,
  //   date: string
  // ) => {
  //   const momentObj = dateService.convertDateStrToMoment(
  //     `${year}-${month}-${date}`,
  //     API_DATE_FORMAT
  //   );
  //   const slots = dataSource?.[momentObj.format("YYYYMMDD")];

  //   const morningSlots = populateSlots(slots, "09:59 AM", "12:00 PM") || 0;
  //   const afternoonSlots = populateSlots(slots, "11:59 AM", "04:00 PM") || 0;

  //   const morningSlotsLength: number = morningSlots?.length;
  //   const afterNoonSlotsLength: number = afternoonSlots?.length;

  //   return morningSlotsLength + afterNoonSlotsLength > 0 || false;
  // };

  const renderMonthDates = (selectedMonth: string) => {
    const momentObj = dateService.convertDateStrToMoment(
        selectedMonth,
        API_DATE_FORMAT
      ),
      monthDates: string[] = [],
      month = momentObj.format("MM"),
      year = momentObj.format("YYYY"),
      daysInMonth = momentObj.daysInMonth();

    // populate empty weekdays at month start
    for (let i = 0; i < momentObj.day(); i++) {
      monthDates.push("");
    }

    // populate month dates
    for (let i = 1; i <= daysInMonth; i++) {
      monthDates.push(momentObj.format("DD"));
      momentObj.add(1, "days");
    }

    return (
      <div className="grid grid-cols-7 gap-4 mt-6 md:gap-6 place-items-center">
        <p className="text-xs text-gray-400 uppercase">Sun</p>
        <p className="text-xs text-gray-400 uppercase">Mon</p>
        <p className="text-xs text-gray-400 uppercase">Tue</p>
        <p className="text-xs text-gray-400 uppercase">Wed</p>
        <p className="text-xs text-gray-400 uppercase">Thr</p>
        <p className="text-xs text-gray-400 uppercase">Fri</p>
        <p className="text-xs text-gray-400 uppercase">Sat</p>
        {monthDates.map((date, index) => {
          const monthDate = `${year}-${month}-${date}`,
            // doesSlotsExists = doesSlotExist(dataSource, year, month, date);
            doesSlotsExists = dataSource?.[`${year}${month}${date}`];
          return !date ? (
            <span key={index}></span>
          ) : (
            <button
              key={index}
              disabled={!doesSlotsExists}
              onClick={() => onDateSelect(monthDate)}
              className={`flex items-center justify-center w-8 h-8 text-base text-gray-800 transition-colors duration-300 rounded-full md:text-lg hover:text-white hover:bg-primary-800 focus:outline-none ${
                monthDate === selectedDate ? "bg-primary-800 text-white" : ""
              } disabled:opacity-40 disabled:pointer-events-none`}>
              {date}
            </button>
          );
        })}
      </div>
    );
  };

  const moveLeft = () => {
    const momentObj = dateService
      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
      .subtract(1, "months");
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  };

  const moveRight = () => {
    const momentObj = dateService
      .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
      .add(1, "months");
    setSelectedMonth(dateService.getStartOfMonth(momentObj));
  };

  useEffect(() => {
    // get timezone from browser
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const patientInfoWithTimezone = {
      ...consumer,
      timeZone: timezone,
    };

    setConsumer(patientInfoWithTimezone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConsumer]);

  const handleSubmit = async (slot: IEventSlot) => {
    if (!slot) {
      toast.error("Please select preferred location and slot");
      return null;
    }
    setErrorMessage(null);
    await networkService
      .post(`/api/scheduling/patients/${consumer.id}/reschedule`, {
        rescheduledAppointmentToken: previousAppointmentData.token,
        newAppointment: {
          appointmentTypeId: previousAppointmentData?.appointmentTypeId,
          patientId: previousAppointmentData.patientId,
          date: slot.date,
          startTime: slot.startTime,
          endTime: slot.endTime,
          timeZone: consumer?.timeZone,
          note: `${selectedAddress?.title}:  ${selectedAddress.line1}, ${selectedAddress.line2}, ${selectedAddress.country} ${selectedAddress.zipCode}`,
        },
      })
      .then((res: any) => {
        const valuesToLog = [
          // res.id,
          // res.appointmentTypeId,
          res.patientId,
          res.date,
          res.startTime,
          res.endTime,
          // res.startDateTime,
          // res.endDateTime,
          res.note,
          res.locationType,
          res.locationInfo,
          res.status,
          // res.externalId,
          // res.appointmentType.id,
          res.appointmentType.startDate,
          res.appointmentType.endDate,
          res.appointmentType.name,
          res.appointmentType.description,
          // res.appointmentType.schedulerSystemUserId,
          res.appointmentType.durationInMinutes,
          res.appointmentType.locationType,
          // res.appointmentType.externalId,
          // res.appointmentType.userId,
        ];

        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request:  /api/scheduling/patients/${consumer.id}/reschedule`,
          eventSubType: "Reschedule appointment",
          eventData:
            "successfully booked appointment, navigating to /reschedule-appointment-success",
        });
        navigate(`/reschedule-appointment-success`, {
          state: {
            location: selectedAddress,
            date: slot.date,
            startDateTime: slot.startDateTime,
            endDateTime: slot.endDateTime,
            valuesToLog,
          },
        });
      })
      .catch((err) => {
        loggerService.error(err);
        websocketLoggerService.sendMessage({
          ...authenticatedUser,
          eventType: `API request:  POST /api/scheduling/patients/${consumer.id}/reschedule`,
          eventSubType: "Reschedule appointment",
          eventData: "Reschedule appointmentfailed",
        });
        toast.error(getUserFriendlyErrorMessage(err.message));
        setErrorMessage(getUserFriendlyErrorMessage(err.message));
      });
  };

  return (
    <Backdrop>
      <div className="p-10 py-5 max-lg:p-2 max-lg:w-full w-full">
        <section className="relative flex flex-col px-10 max-lg:p-0  ">
          {isWithin24Hours(previousAppointmentData?.startDateTime) && (
            <div className="bg-red-50 border-l-4 border-red-600 text-yellow-700 p-4 mb-5 max-lg:text-sm">
              <p className="font-bold">Please note:</p>
              <p>
                Appointments cannot be rescheduled within 24 hours of the
                scheduled session start time.
              </p>
              <p>
                If you need to make any changes or have any questions, please
                feel free to reach out to the Azodha team at{" "}
                <a
                  href="mailto:rippleadmin@sheareshealthcare.com.sg"
                  className="text-primary-blue">
                  support@azodha.com
                </a>
              </p>
            </div>
          )}
          <div className="flex items-center justify-start w-full gap-2 mt-4 bg-yellow-50  p-4 rounded-md">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="fill-yellow-900"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
            </svg>

            <span className="text-black">
              Your previous booking will be cancelled
            </span>
          </div>
          <div className=" rounded bg-gray-50 w-full p-3 mt-2">
            <div className=" rounded-md flex flex-col gap-4">
              <div className="flex items-center gap-6 rounded-t-md">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_140_1275)">
                    <path
                      d="M6.382 6.13109C7.97575 4.85415 9.95781 4.15983 12 4.16309C14.125 4.16309 16.078 4.89909 17.618 6.13109L19.071 4.67809L20.485 6.09209L19.032 7.54509C20.0897 8.86923 20.7521 10.4651 20.9428 12.1491C21.1336 13.833 20.845 15.5366 20.1103 17.0638C19.3756 18.591 18.2246 19.8797 16.7898 20.7817C15.355 21.6836 13.6947 22.1621 12 22.1621C10.3053 22.1621 8.64498 21.6836 7.2102 20.7817C5.77541 19.8797 4.62443 18.591 3.88973 17.0638C3.15502 15.5366 2.86644 13.833 3.0572 12.1491C3.24796 10.4651 3.9103 8.86923 4.96801 7.54509L3.51501 6.09309L4.92901 4.67909L6.382 6.13209V6.13109ZM12 20.1631C12.9193 20.1631 13.8295 19.982 14.6788 19.6302C15.5281 19.2785 16.2997 18.7628 16.9498 18.1128C17.5998 17.4628 18.1154 16.6911 18.4672 15.8419C18.8189 14.9926 19 14.0823 19 13.1631C19 12.2438 18.8189 11.3336 18.4672 10.4843C18.1154 9.63502 17.5998 8.86335 16.9498 8.21334C16.2997 7.56333 15.5281 7.04771 14.6788 6.69593C13.8295 6.34415 12.9193 6.16309 12 6.16309C10.1435 6.16309 8.36301 6.90058 7.05026 8.21334C5.7375 9.52609 5.00001 11.3066 5.00001 13.1631C5.00001 15.0196 5.7375 16.8001 7.05026 18.1128C8.36301 19.4256 10.1435 20.1631 12 20.1631ZM13 12.1631H16L11 18.6631V14.1631H8.00001L13 7.65809V12.1631ZM8.00001 1.16309H16V3.16309H8.00001V1.16309Z"
                      fill="#AFAFAF"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_140_1275">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.163086)"
                      />
                    </clipPath>
                  </defs>
                </svg>

                <div>
                  {" "}
                  {moment(previousAppointmentData?.startDateTime).format(
                    "hh:mm A"
                  )}{" "}
                  -
                  {moment(previousAppointmentData?.endDateTime).format(
                    "hh:mm A"
                  )}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_140_1276)">
                    <path
                      d="M9 1.16309V3.16309H15V1.16309H17V3.16309H21C21.2652 3.16309 21.5196 3.26844 21.7071 3.45598C21.8946 3.64352 22 3.89787 22 4.16309V20.1631C22 20.4283 21.8946 20.6827 21.7071 20.8702C21.5196 21.0577 21.2652 21.1631 21 21.1631H3C2.73478 21.1631 2.48043 21.0577 2.29289 20.8702C2.10536 20.6827 2 20.4283 2 20.1631V4.16309C2 3.89787 2.10536 3.64352 2.29289 3.45598C2.48043 3.26844 2.73478 3.16309 3 3.16309H7V1.16309H9ZM20 10.1631H4V19.1631H20V10.1631ZM15.036 11.2991L16.45 12.7131L11.5 17.6631L7.964 14.1271L9.38 12.7131L11.501 14.8351L15.037 11.2991H15.036ZM7 5.16309H4V8.16309H20V5.16309H17V6.16309H15V5.16309H9V6.16309H7V5.16309Z"
                      fill="#AFAFAF"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_140_1276">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.163086)"
                      />
                    </clipPath>
                  </defs>
                </svg>

                <div>
                  {moment(previousAppointmentData?.startDateTime).format(
                    "dddd DD MMMM YYYY"
                  )}{" "}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_194_3199)">
                    <path
                      d="M12 20.8999L16.95 15.9499C17.9289 14.9709 18.5955 13.7236 18.8656 12.3658C19.1356 11.0079 18.9969 9.60052 18.4671 8.32148C17.9373 7.04244 17.04 5.94923 15.8889 5.18009C14.7378 4.41095 13.3844 4.00043 12 4.00043C10.6156 4.00043 9.26222 4.41095 8.11109 5.18009C6.95996 5.94923 6.06275 7.04244 5.53292 8.32148C5.00308 9.60052 4.86442 11.0079 5.13445 12.3658C5.40449 13.7236 6.07111 14.9709 7.05 15.9499L12 20.8999ZM12 23.7279L5.636 17.3639C4.37734 16.1052 3.52019 14.5016 3.17293 12.7558C2.82567 11.0099 3.00391 9.20035 3.6851 7.55582C4.36629 5.91129 5.51984 4.50569 6.99988 3.51677C8.47992 2.52784 10.22 2 12 2C13.78 2 15.5201 2.52784 17.0001 3.51677C18.4802 4.50569 19.6337 5.91129 20.3149 7.55582C20.9961 9.20035 21.1743 11.0099 20.8271 12.7558C20.4798 14.5016 19.6227 16.1052 18.364 17.3639L12 23.7279ZM12 12.9999C12.5304 12.9999 13.0391 12.7892 13.4142 12.4141C13.7893 12.0391 14 11.5304 14 10.9999C14 10.4695 13.7893 9.96078 13.4142 9.58571C13.0391 9.21064 12.5304 8.99992 12 8.99992C11.4696 8.99992 10.9609 9.21064 10.5858 9.58571C10.2107 9.96078 10 10.4695 10 10.9999C10 11.5304 10.2107 12.0391 10.5858 12.4141C10.9609 12.7892 11.4696 12.9999 12 12.9999ZM12 14.9999C10.9391 14.9999 9.92172 14.5785 9.17158 13.8283C8.42143 13.0782 8 12.0608 8 10.9999C8 9.93906 8.42143 8.92164 9.17158 8.17149C9.92172 7.42135 10.9391 6.99992 12 6.99992C13.0609 6.99992 14.0783 7.42135 14.8284 8.17149C15.5786 8.92164 16 9.93906 16 10.9999C16 12.0608 15.5786 13.0782 14.8284 13.8283C14.0783 14.5785 13.0609 14.9999 12 14.9999Z"
                      fill="#8F8F8F"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_194_3199">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>

                <div className="flex flex-col">
                  <span> {previousAppointmentData?.note}</span>
                </div>
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mt-4">
            Reschedule the appointment
          </h1>

          <p className="mt-5">Preferred Location</p>
          <div className="flex mt-2 flex-wrap gap-5">
            {addresses?.map((address, index) => (
              <div
                className={`border  rounded-md w-60 px-2 py-3 cursor-pointer flex gap-1 max-lg:w-full ${
                  selectedAddress?.id === address.id
                    ? "border-primary-800 bg-[#EAEBFF]"
                    : "border-gray-400 bg-white"
                }`}
                onClick={() => {
                  setSelectedAddress(address);
                }}
                key={address.id}>
                <input
                  id="default-radio-1"
                  type="radio"
                  checked={selectedAddress?.id === address.id}
                  value={address.title}
                  onChange={() => {
                    setSelectedAddress(address);
                  }}
                  name="default-radio"
                  className="w-4 h-4 text-primary-900 bg-white border-gray-300  mt-1"
                />
                <label
                  htmlFor={address.title}
                  className="ml-2 text-sm font-medium ">
                  <p className="font-bold">{address.title}</p>
                  {address?.line1}, {address?.line2}, {address?.country}{" "}
                  {address?.zipCode}
                </label>
              </div>
            ))}
          </div>
          {!selectedAddress && addresses && (
            <div className=" mt-8 w-full flex items-center justify-start gap-2 bg-yellow-50 p-4 rounded-md">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="fill-yellow-900"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
              </svg>

              <span className="text-black">
                Please select a location to check appointment availability.
              </span>
            </div>
          )}
          <main className="mt-8 lg:flex flex-col lg:items-start ">
            <div className="p-6 bg-white border border-ripple-gray-200 rounded-xl">
              <div className="flex items-center justify-between w-full mx-auto text-lg text-gray-800 focus:outline-none">
                <button onClick={moveLeft} className=" focus:outline-none">
                  <svg
                    width="16"
                    height="13"
                    viewBox="0 0 16 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M16 6.49983C16 6.14357 15.7311 5.85461 15.4001 5.85461H2.04836L6.46786 1.10143C6.70223 0.849364 6.70223 0.440879 6.46786 0.189047C6.23391 -0.0630156 5.85347 -0.0630156 5.61953 0.189047L0.175776 6.04381C-0.0585919 6.29587 -0.0585919 6.70436 0.175776 6.95619L5.61953 12.811C5.7365 12.9372 5.8901 13 6.04369 13C6.19729 13 6.35088 12.937 6.46786 12.811C6.70223 12.5589 6.70223 12.1504 6.46786 11.8986L2.04879 7.1454H15.4005C15.7318 7.1454 16 6.85608 16 6.49983Z"
                      fill="#0038AE"
                    />
                  </svg>
                </button>

                <span>
                  {dateService
                    .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
                    .format("MMMM")}{" "}
                  {dateService
                    .convertDateStrToMoment(selectedMonth, API_DATE_FORMAT)
                    .format("YYYY")}{" "}
                </span>

                <button onClick={moveRight} className=" focus:outline-none">
                  <svg
                    width="16"
                    height="13"
                    viewBox="0 0 16 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.68233e-07 6.50017C5.37088e-07 6.85643 0.268894 7.14539 0.599927 7.14539L13.9516 7.14539L9.53214 11.8986C9.29777 12.1506 9.29777 12.5591 9.53214 12.811C9.76609 13.063 10.1465 13.063 10.3805 12.811L15.8242 6.95619C16.0586 6.70413 16.0586 6.29564 15.8242 6.04381L10.3805 0.189044C10.2635 0.0627885 10.1099 -5.14929e-07 9.95631 -5.28357e-07C9.80271 -5.41784e-07 9.64912 0.0630192 9.53214 0.189044C9.29777 0.441107 9.29777 0.849593 9.53214 1.10142L13.9512 5.8546L0.599498 5.8546C0.268244 5.8546 5.99378e-07 6.14392 5.68233e-07 6.50017Z"
                      fill="#0038AE"
                    />
                  </svg>
                </button>
              </div>

              {renderMonthDates(selectedMonth)}
            </div>
            <div
              style={{
                visibility:
                  !selectedSlot && selectedAddress ? "visible" : "hidden",
              }}
              className="flex items-center justify-start w-full gap-2 mt-4 bg-yellow-50  p-4 rounded-md">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="fill-yellow-900"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
              </svg>

              <span className="text-black">
                Please select a time slot to book an appointment.
              </span>
            </div>
            <div className="mt-8 pb-10 w-full ">{filteredSlots}</div>
          </main>
          {selectedSlot && (
            <div className="flex items-center my-2 justify-start gap-2 bg-yellow-50 p-4  rounded-md">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="fill-yellow-900"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M13.936 0.5L19.5 6.067V13.937L13.936 19.5H6.066L0.5 13.936V6.066L6.066 0.5H13.936ZM13.107 2.5H6.894L2.501 6.895V13.108L6.894 17.502H13.107L17.501 13.108V6.894L13.107 2.501V2.5ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z" />
              </svg>

              <span className="text-black">
                Please review your submission details before submitting this
                form.
              </span>
            </div>
          )}
          {errorMessage && <ErrorWarning message={errorMessage} />}
          <footer className=" w-2/5 py-8 flex items-center justify-between max-lg:w-full max-lg:pr-16">
            <button
              type="button"
              disabled={
                !selectedSlot ||
                isWithin24Hours(previousAppointmentData?.startDateTime)
              }
              onClick={async () => await handleSubmit(selectedSlot)}
              className="w-max bg-primary-500  px-6 py-4 mt-4 text-white rounded font-bold disabled:bg-gray-400 disabled:cursor-not-allowed">
              Reschedule Now
            </button>
          </footer>
        </section>
      </div>
    </Backdrop>
  );
};

export default RescheduleSession;

const SlotsTemplate = ({ title, slots, icon }) => {
  return (
    <>
      <div className="flex gap-x-2 items-center">
        {icon}

        <h1>{title}</h1>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4 mb-4 md:grid-cols-3">
        {slots}
      </div>
    </>
  );
};
